/**
 * useAppOperations.ts — data + action hooks for the App Operations Board
 * (ADR-075 step 5, D11; component-app-operations-board)
 *
 * The board is the per-app management surface for operators / tenant admins.
 * It READS deployment history (GET /apps/:app/deployments — the same endpoint
 * the propagation workflow uses), branch dev-states and health, and TRIGGERS
 * verification, change requests and UAT registration. It has NO approve or
 * push-back capability — approval is a tenant-store action (OQ1b); this module
 * deliberately imports nothing from useUatDecision.
 *
 * All requests carry the Keycloak Bearer token; every endpoint re-checks the
 * operator/tenant-admin role SERVER-SIDE (the SPA guard is UX only). The SPA
 * holds no GitHub / Doppler / Keycloak-admin credentials — those live behind
 * the broker API.
 */

import { useCallback, useEffect, useState } from 'react';

// ── Deployments (Part A) ─────────────────────────────────────────────────────

export interface BoardDeployment {
  deploy_no: number;
  deploy_date: string;
  release_tag: string;
  image_ref: string;
  status: 'uat' | 'published' | 'pushed_back' | 'superseded_published' | (string & {});
  queued: boolean;
  decided_by: string | null;
  decided_at: string | null;
  decision_comment: string | null;
}

export type BoardFetchState<T> =
  | { tag: 'loading' }
  | { tag: 'loaded'; data: T }
  | { tag: 'error'; errorCode?: string };

function useBoardFetch<T>(url: string | null, token: string | null, refreshKey = 0): BoardFetchState<T> {
  const [state, setState] = useState<BoardFetchState<T>>({ tag: 'loading' });
  useEffect(() => {
    if (!url || !token) return;
    let cancelled = false;
    setState({ tag: 'loading' });
    (async () => {
      try {
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        if (!res.ok) {
          setState({ tag: 'error', errorCode: `HTTP_${res.status}` });
          return;
        }
        const data = (await res.json()) as T;
        if (!cancelled) setState({ tag: 'loaded', data });
      } catch {
        if (!cancelled) setState({ tag: 'error', errorCode: 'NETWORK' });
      }
    })();
    return () => { cancelled = true; };
  }, [url, token, refreshKey]);
  return state;
}

export function useDeployments(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<{ deployments: BoardDeployment[] }>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/deployments`, token, refreshKey,
  );
}

// ── Branches (Part A) ────────────────────────────────────────────────────────

export interface BoardBranch { branch: string; state: string; open: boolean }

export function useBranchState(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<{ app: string; branches: BoardBranch[] }>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/branch-state`, token, refreshKey,
  );
}

// ── Health & verification (Part B) ───────────────────────────────────────────

export interface HealthCheck {
  name: string;
  ok: boolean;
  status: number | null;
  latency_ms: number | null;
  detail?: string;
}
export interface AppHealth {
  app: string;
  overall: 'healthy' | 'degraded' | 'unreachable';
  checks: HealthCheck[];
}

export function useAppHealth(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<AppHealth>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/health`, token, refreshKey,
  );
}

export interface VerifyResult {
  pass: number; fail: number; failed_gates: string[];
}
export interface VerifyStatus {
  last: null | {
    dispatched_at: string;
    run_url?: string | null;
    result: VerifyResult | null;   // null while a run is still in flight
    raw_line?: string | null;
  };
}

export function useVerifyStatus(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<VerifyStatus>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/verify`, token, refreshKey,
  );
}

// ── Access management (Part D) ───────────────────────────────────────────────

export interface AccessUser {
  id: string;
  username: string;
  email: string;
  uat_approver: boolean;
  tester: boolean;
}

export function useAccessList(appId: string, token: string | null, refreshKey = 0) {
  return useBoardFetch<{ users: AccessUser[]; realm_note: string }>(
    `/api/broker/v1/apps/${encodeURIComponent(appId)}/access`, token, refreshKey,
  );
}

// ── Actions (Parts B/C/D triggers) ───────────────────────────────────────────

export type ActionState =
  | { tag: 'idle' }
  | { tag: 'submitting'; action: string }
  | { tag: 'done'; action: string; message?: string }
  | { tag: 'error'; action: string; message: string };

/**
 * One shared action runner: POST/DELETE to a board endpoint, declared result
 * or declared failure — never a silent state. All board mutations audit
 * server-side (lifecycle_events / state_transition).
 */
export function useBoardAction(appId: string, token: string | null, onChanged?: () => void) {
  const [state, setState] = useState<ActionState>({ tag: 'idle' });

  const run = useCallback(async (
    action: string,
    path: string,
    opts?: { method?: 'POST' | 'DELETE'; body?: Record<string, unknown> },
  ): Promise<boolean> => {
    if (!token) { setState({ tag: 'error', action, message: 'not-authenticated' }); return false; }
    setState({ tag: 'submitting', action });
    try {
      const res = await fetch(
        `/api/broker/v1/apps/${encodeURIComponent(appId)}${path}`,
        {
          method: opts?.method ?? 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          ...(opts?.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
        },
      );
      const body = await res.json().catch(() => ({})) as { declared?: string; error?: string };
      if (!res.ok) {
        setState({ tag: 'error', action, message: body.error ?? `HTTP_${res.status}` });
        return false;
      }
      setState({ tag: 'done', action, message: body.declared });
      onChanged?.();
      return true;
    } catch {
      setState({ tag: 'error', action, message: 'NETWORK' });
      return false;
    }
  }, [appId, token, onChanged]);

  const reset = useCallback(() => setState({ tag: 'idle' }), []);
  return { state, run, reset };
}
